import type { Column, TableOptions } from '../../hooks/useTable/types.js';
import type { DataTableV2RowData } from '../../public.api.js';
import { ROW_DETAILS_COLUMN } from '../RowDetails/RowDetails.js';
import { ROW_DRAG_COLUMN } from '../RowOrder/RowOrder.js';
import { ROW_SELECTION_COLUMN } from '../RowSelection/RowSelection.js';
import { ROW_ACTIONS_COLUMN } from '../UserActions/RowActions.js';

/**
 * @internal
 */
export interface DataTableV2ColumnPinningInstance<
  TData extends DataTableV2RowData,
> {
  // Overriding the type of the getLeftLeafColumns with our internal Column type, to avoid conflicts
  // between react table and our types.
  getLeftLeafColumns: () => Column<TData, unknown>[];
  // Overriding the type of the getRightLeafColumns with our internal Column type, to avoid conflicts
  // between react table and our types.
  getRightLeafColumns: () => Column<TData, unknown>[];
  // Overriding the type of the getCenterLeafColumns with our internal Column type, to avoid conflicts
  // between react table and our types.
  getCenterLeafColumns: () => Column<TData, unknown>[];
}

/**
 * Configuration hook for the DataTableV2 column pinning feature.
 * @internal
 */
export function useColumnPinning<TData extends DataTableV2RowData>(
  options: TableOptions<TData>,
): void {
  const leftPinnedColumns: string[] = [];
  const rightPinnedColumns: string[] = [];

  // The order in which column IDs are pushed into the array is important,
  // as it determines the fixed display order of built-in columns in the table.
  // This order must not be changed, as it affects the layout.
  if (options.enableRowOrderDragAndDrop && ROW_DRAG_COLUMN.id) {
    leftPinnedColumns.push(ROW_DRAG_COLUMN.id);
  }
  if (options.enableRowSelection && ROW_SELECTION_COLUMN.id) {
    leftPinnedColumns.push(ROW_SELECTION_COLUMN.id);
  }
  if (options.enableRowDetails && ROW_DETAILS_COLUMN.id) {
    leftPinnedColumns.push(ROW_DETAILS_COLUMN.id);
  }
  if (options.enableRowActions && ROW_ACTIONS_COLUMN.id) {
    rightPinnedColumns.push(ROW_ACTIONS_COLUMN.id);
  }

  options.state = {
    ...options.state,
    columnPinning: {
      left: leftPinnedColumns,
      right: rightPinnedColumns,
    },
  };
}
